import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  Checkbox,
  Button,
  FormHelperText,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RxPreviewDto } from "../../../../../../../api-client";
import useStore from "../../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../../stores/Notification/notificationStore";
import "../../../../DirectBilling/common/helpers/css/direct-bill-info.css";

interface TransactionSpotlightSearchProps {
  accountId: string;
  onRxsSelected: (rxPreviews: RxPreviewDto[]) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isOpen: boolean;
  onClose: () => void;
}

const TransactionSpotlightSearch: React.FC<TransactionSpotlightSearchProps> = ({
  accountId,
  onRxsSelected,
  isLoading,
  setIsLoading,
  isOpen,
  onClose,
}) => {
  // Refs for the input fields
  // const yearInputRef = useRef<HTMLInputElement>(null);
  // const numberInputRef = useRef<HTMLInputElement>(null);

  // State for segmented transaction number
  // const [yearPart, setYearPart] = useState("");
  // const [numberPart, setNumberPart] = useState("");

  // Simplified state for full transaction number
  const [fullTransactionNumber, setFullTransactionNumber] = useState("");

  const [transactionNumberError, setTransactionNumberError] = useState("");
  const [rxPreviews, setRxPreviews] = useState<RxPreviewDto[]>([]);
  const [selectedRxs, setSelectedRxs] = useState<RxPreviewDto[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const TransactionsStore = useStore(TransactionsContext);
  const { resolveTransactionNumber, getRxPreviewData } = TransactionsStore;

  // Get the full transaction number from the segmented parts
  // const getFullTransactionNumber = (): string => {
  //   return `V${yearPart}-${numberPart}`;
  // };

  // Simplified function to return the full transaction number
  const getFullTransactionNumber = (): string => {
    return fullTransactionNumber;
  };

  const handleSearch = async () => {
    const transactionNumber = getFullTransactionNumber();

    // if (!yearPart || !numberPart) {
    //   setTransactionNumberError("Bitte geben Sie eine vollständige Transaktionsnummer ein");
    //   return;
    // }

    if (!fullTransactionNumber.trim()) {
      setTransactionNumberError("Bitte geben Sie eine vollständige Transaktionsnummer ein");
      return;
    }

    // Clear any existing preview data
    setRxPreviews([]);
    setSelectedRxs([]);
    setShowPreview(false);

    setIsLoading(true);
    setTransactionNumberError("");

    try {
      // First resolve the transaction number to get the transaction ID
      const transactionResult = await resolveTransactionNumber(accountId, transactionNumber);

      if (transactionResult && transactionResult.transactionId) {
        // Get RX preview data for this transaction
        const rxPreviewData = await getRxPreviewData(transactionResult.transactionId);

        if (rxPreviewData && (Array.isArray(rxPreviewData) ? rxPreviewData.length > 0 : true)) {
          setRxPreviews(Array.isArray(rxPreviewData) ? rxPreviewData : [rxPreviewData]);
          setShowPreview(true);
        } else {
          notificationStore.showMessage(
            "Keine Rezeptdaten für diese Transaktion gefunden",
            "warning"
          );
        }
      } else {
        setTransactionNumberError("Transaktion nicht gefunden");
      }
    } catch (error: any) {
      let errorMessage = error?.response?.data?.message || error.message || "Fehler bei der Suche";
      console.error("Error searching for transaction", error);
      notificationStore.showMessage(errorMessage, "error");
      setTransactionNumberError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleRx = (rx: RxPreviewDto) => {
    if (selectedRxs.some((selected) => selected.id === rx.id)) {
      setSelectedRxs(selectedRxs.filter((selected) => selected.id !== rx.id));
    } else {
      setSelectedRxs([...selectedRxs, rx]);
    }
  };

  const handleSelectAll = () => {
    if (selectedRxs.length === rxPreviews.length) {
      setSelectedRxs([]);
    } else {
      setSelectedRxs([...rxPreviews]);
    }
  };

  const handleCancel = () => {
    // setYearPart("");
    // setNumberPart("");
    setFullTransactionNumber("");
    setRxPreviews([]);
    setSelectedRxs([]);
    setShowPreview(false);
    onClose();
  };

  const handleCompleteTransaction = () => {
    if (selectedRxs.length === 0) {
      notificationStore.showMessage("Bitte wählen Sie mindestens ein Rezept aus", "warning");
      return;
    }

    onRxsSelected(selectedRxs);
    // setYearPart("");
    // setNumberPart("");
    setFullTransactionNumber("");
    setRxPreviews([]);
    setSelectedRxs([]);
    setShowPreview(false);
    onClose();
  };

  // Handle input changes and auto-focus to next field
  // const handleYearInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   // Only allow 2 digits and automatically move to next field when 2 digits are entered
  //   if (/^\d{0,2}$/.test(value)) {
  //     setYearPart(value);
  //     if (value.length === 2 && numberInputRef.current) {
  //       numberInputRef.current.focus();
  //     }
  //   }
  // };

  // const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   // Only allow numbers
  //   if (/^\d*$/.test(value)) {
  //     setNumberPart(value);
  //   }
  // };

  // Simplified handler for full transaction number input
  const handleTransactionNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullTransactionNumber(e.target.value);
  };

  // Handle backspace to move to previous field
  // const handleYearKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === "Enter" && yearPart.length === 2 && numberInputRef.current) {
  //     numberInputRef.current.focus();
  //   }
  // };

  // const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === "Backspace" && numberPart === "" && yearInputRef.current) {
  //     yearInputRef.current.focus();
  //   } else if (e.key === "Enter" && yearPart && numberPart) {
  //     handleSearch();
  //   }
  // };

  // Simplified key handler for full transaction number input
  const handleTransactionNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && fullTransactionNumber.trim()) {
      handleSearch();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 2.5,
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #eaeaea",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          fontWeight="600"
          color="#003059"
        >
          Transaktion suchen
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          size="small"
          sx={{
            color: "grey.500",
            "&:hover": { color: "secondary.main" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 3,
          backgroundColor: "#fff",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#a1a1a1",
          },
        }}
      >
        <FormControl
          error={!!transactionNumberError}
          fullWidth
        >
          <Typography
            variant="subtitle1"
            gutterBottom
            fontWeight="500"
            color="#003059"
          >
            Transaktion-Nummer eingeben
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              value={fullTransactionNumber}
              onChange={handleTransactionNumberChange}
              onKeyDown={handleTransactionNumberKeyDown}
              variant="outlined"
              placeholder="z.B. V25-1234"
              fullWidth
              sx={{
                mr: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#fff",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                    "& fieldset": {
                      borderColor: "#003059",
                      borderWidth: "2px",
                    },
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "12px 16px",
                  fontSize: "16px",
                  fontWeight: "500",
                },
              }}
            />
            {/* Old segmented input - commented out */}
            {/* <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: `1px solid ${transactionNumberError ? "#d32f2f" : "#c4c4c4"}`,
                borderRadius: "8px",
                p: 1.5,
                width: "fit-content",
                mr: 2,
                background: "#f8f9fa",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                transition: "all 0.2s ease-in-out",
                "&:focus-within": {
                  borderColor: "#003059",
                  borderWidth: "2px",
                  boxShadow: "0 4px 8px rgba(25, 118, 210, 0.15)",
                  background: "#fff",
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  mx: 1,
                  userSelect: "none",
                  fontWeight: "bold",
                  color: "#003059",
                }}
              >
                V
              </Typography>
              <TextField
                inputRef={yearInputRef}
                value={yearPart}
                onChange={handleYearInputChange}
                onKeyDown={handleYearKeyDown}
                variant="standard"
                placeholder="JJ"
                inputProps={{
                  maxLength: 2,
                  style: {
                    textAlign: "center",
                    width: "30px",
                    padding: "4px 0",
                    fontWeight: "500",
                    fontSize: "18px",
                  },
                }}
                sx={{
                  "& .MuiInput-underline:before": { borderBottom: "1px solid #003059" },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" },
                  "& .MuiInput-underline:after": { borderBottom: "none" },
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  mx: 1,
                  userSelect: "none",
                  fontWeight: "bold",
                  color: "#666",
                }}
              >
                -
              </Typography>
              <TextField
                inputRef={numberInputRef}
                value={numberPart}
                onChange={handleNumberInputChange}
                onKeyDown={handleNumberKeyDown}
                variant="standard"
                placeholder="Nummer"
                inputProps={{
                  style: {
                    width: "80px",
                    padding: "4px 0",
                    fontWeight: "500",
                    fontSize: "18px",
                  },
                }}
                sx={{
                  "& .MuiInput-underline:before": { borderBottom: "1px solid #003059" },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" },
                  "& .MuiInput-underline:after": { borderBottom: "none" },
                }}
              />
            </Box> */}
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading || !fullTransactionNumber.trim()}
              sx={{
                borderRadius: "8px ",
                px: 3,
                py: 1.2,
                textTransform: "none",
                fontWeight: "500",
                boxShadow: "0 3px 5px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 5px 8px rgba(0,0,0,0.15)",
                },
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Suchen"}
            </Button>
          </Box>
          {transactionNumberError && (
            <FormHelperText
              error
              sx={{ mt: 1, fontSize: "14px" }}
            >
              {transactionNumberError}
            </FormHelperText>
          )}
        </FormControl>
      </DialogContent>

      {/* Only show RX preview section if we have results */}
      {showPreview && rxPreviews.length > 0 && (
        <>
          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              borderTop: "1px solid #eaeaea",
              borderBottom: "1px solid #eaeaea",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="600"
              color="#003059"
            >
              Gefundene Rezepte ({rxPreviews.length})
            </Typography>
            <Button
              onClick={handleSelectAll}
              size="small"
              sx={{
                color: "#003059",
                fontWeight: "500",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#003059",
                },
              }}
            >
              {selectedRxs.length === rxPreviews.length ? "Alle abwählen" : "Alle auswählen"}
            </Button>
          </Box>

          <DialogContent
            sx={{
              p: 0,
              maxHeight: "300px",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a1a1a1",
              },
            }}
          >
            {rxPreviews.map((rx) => (
              <Box
                key={rx.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  p: 1.5,
                  borderBottom: "1px solid #eee",
                  transition: "background-color 0.2s",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  backgroundColor: selectedRxs.some((selected) => selected.id === rx.id)
                    ? "rgba(0, 48, 89, 0.05)"
                    : "transparent",
                }}
              >
                <Checkbox
                  checked={selectedRxs.some((selected) => selected.id === rx.id)}
                  onChange={() => handleToggleRx(rx)}
                  color="primary"
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: 24,
                    },
                  }}
                />
                <Box sx={{ ml: 1.5, flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body1"
                        fontWeight="500"
                        sx={{ mr: 1 }}
                      >
                        Rezept: {rx.rxNumber}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", ml: 1 }}>
                        {rx.therapies?.map((therapy, index) => (
                          <span
                            key={index}
                            className="therapy-tag"
                            style={{
                              fontSize: "0.7rem",
                              padding: "0.1em 0.4em",
                              margin: "0 0.15rem",
                            }}
                          >
                            {therapy}
                          </span>
                        ))}
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Datum:{" "}
                      {rx.rxDate
                        ? new Date(rx.rxDate.toString()).toLocaleDateString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Patient: {rx.patientFirstName} {rx.patientLastName}
                  </Typography>
                </Box>
              </Box>
            ))}
          </DialogContent>
        </>
      )}

      <DialogActions
        sx={{
          p: 2.5,
          backgroundColor: "#f8f9fa",
          borderTop: "1px solid #eaeaea",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={handleCancel}
          sx={{
            mr: 1.5,
            px: 3,
            py: 1,
            fontWeight: "500",
            textTransform: "none",
            color: "#000000",
            "&:hover": {
              backgroundColor: "secondary.main",
            },
            borderRadius: "8px",
          }}
        >
          Abbrechen
        </Button>
        {showPreview && rxPreviews.length > 0 && (
          <Button
            variant="contained"
            onClick={handleCompleteTransaction}
            disabled={selectedRxs.length === 0}
            sx={{
              px: 3,
              py: 1,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "500",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              "&:hover": {
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
              },
            }}
          >
            {selectedRxs.length} Rezept(e) hinzufügen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransactionSpotlightSearch;
